import React, { useRef, useState } from 'react';
import { Form, Input, Row, SonForm, SubmitButton, useForm } from '../../../_lib/Form2';
import { KeyEvent } from '../../../types/KeyEvent';
import { SelectList } from '../../../_lib/Form2/elements/SelectList/SelectList';
import { SelectListItemHiddenInput } from '../../../_lib/Form2/elements/SelectList/SelectListItemHiddenInput';
import { useSonTranslation } from '../../../translation/useSonTranslation';
import { BtnModifier } from '../../../components/molecules/Btn';
import { TooltipPlacement } from '../../../components/atoms/Tooltip';
import { ButtonIcon } from '../../../components/molecules/Btn/Button/ButtonIcon';
import { Icons } from '../../../components/atoms/SVG';
import { Button } from '../../../components/molecules/Btn/Button';
import { useHtmlValidId } from '../../../hooks/useHtmlValidId';

type PageMembershipMatchingAttributesEditForm = {
  form: SonForm<any>;
  onSuccess: () => void;
};

export function PageMembershipMatchingAttributesEditForm({
  form,
  onSuccess,
}: PageMembershipMatchingAttributesEditForm) {
  const { t } = useSonTranslation('PageMembershipMatchingAttributes');
  const id = 'page-membership-matching-attributes-edit-form' + useHtmlValidId();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [addButtonDisabled, setAddButtonDisabled] = useState(true);
  const { formProps, fetchState, params } = useForm({
    form,
    onSuccess,
    useJson: true,
  });
  const [attributeList, setAttributeList] = useState([...params.getAllByName('matchingAttributes')]);
  const addAttribute = (input: HTMLInputElement) => {
    setAttributeList([
      {
        type: 'hidden',
        name: 'matchingAttributes',
        required: false,
        value: input.value,
        maxLength: input.maxLength,
      },
      ...attributeList]);
    input.value = '';
  };

  return (
    <Form {...formProps}>
      <Row modifier="display-flex">
        <Input
          ref={inputRef}
          // hard coded maxLength which should be part of the BE JSON,
          // but without an attribute, the form params array is empty and therefore the constraint is missing
          maxLength={100}
          label={t('edit.attributeInputLabel')}
          onChange={(event) => {
            // trimming to avoid entries with spaces at start, end or entire value of spaces
            setAddButtonDisabled(!event.target.value.trim());
          }}
          onKeyDown={(event) => {
            // trimming to avoid entries with spaces at start, end or entire value of spaces
            const trimmedValue = (event.target as HTMLInputElement).value.trim();
            if (event.key === KeyEvent.Enter && trimmedValue) {
              event.preventDefault();
              event.stopPropagation();

              addAttribute(event.target as HTMLInputElement);
            }
          }}
        />
        <div className="page-membership-matching-attributes__attribute-add-button">
          <Button
            type="button"
            title={t('edit.labelAddAttributeButton')}
            modifier={[BtnModifier.MINOR_ACTION, BtnModifier.TRANSPARENT]}
            tooltipPlacement={TooltipPlacement.TOP}
            hideTooltipFromScreenReader
            disabled={addButtonDisabled}
            aria-controls={id}
            onClick={() => {
              if (inputRef.current) {
                addAttribute(inputRef.current);
                setAddButtonDisabled(true);
                inputRef.current.focus();
              }
            }}
          >
            <ButtonIcon
              text={t('edit.labelAddAttributeButton')}
              svgSpriteId={Icons.ADD_PLUS}
            />
          </Button>
        </div>
      </Row>
      <SelectList
        headline={t('edit.list.headline')}
        noElementsPhrase={t('edit.list.noAttributes')}
        shouldHaveSkipLinks={attributeList.length >= 5}
        id={id}
      >
        {attributeList.map((attribute) => {
          return (
            <SelectListItemHiddenInput
              key={attribute.value}
              ariaControls={id}
              title={attribute.value}
              name={attribute.name}
              value={attribute.value}
              deleteButtonTitle={t('edit.list.item.delete', { itemName: attribute.value })}
              deleteElement={
                () => setAttributeList(attributeList.filter(item => item.value !== attribute.value))
              }
            />
          );
        })}
      </SelectList>
      <SubmitButton
        buttonLabel={t('edit.submitButtonLabel')}
        messageSuccess={t('edit.status.success')}
        messageSending={t('edit.status.sending')}
        messageFail={t('edit.status.fail')}
        fetchState={fetchState}
      />
    </Form>
  );
}
